import type { CSSProperties, FC, ReactNode } from 'react'

import { motion, useInView } from 'motion/react'

import {
    Fragment,
    createElement,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { useIntroReady } from '@/components/generals/intro'

const SOFT_EASE = [0.22, 1, 0.36, 1] as const

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
    y?: number
    amount?: number
}

/**
 * Fades and rises gently into view, once.
 */
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0, y = 32, amount = 0.25 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true, amount })
    const ready = useIntroReady()

    return (
        <motion.div
            ref={ref}
            className={className}
            initial={{ opacity: 0, y }}
            animate={inView && ready ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 1.1, delay, ease: SOFT_EASE }}
        >
            {children}
        </motion.div>
    )
}

type RevealTag = 'h1' | 'h2' | 'h3' | 'p' | 'blockquote' | 'span'

interface RevealTextProps {
    text: string
    as?: RevealTag
    className?: string
    delay?: number
    lineStagger?: number
    id?: string
}

/**
 * Headline that rises line by line out of a mask. Lines are measured after
 * layout, so the stagger follows however the text wraps on each screen.
 */
export const RevealText: FC<RevealTextProps> = ({ text, as = 'h2', className, delay = 0, lineStagger = 0.11, id }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const inView = useInView(ref, { once: true, amount: 0.35 })
    const ready = useIntroReady()
    const words = useMemo(() => text.split(' '), [text])
    const [lines, setLines] = useState<number[]>([])

    useEffect(() => {
        const element = ref.current

        if (!element) return

        const measure = () => {
            const masks = element.querySelectorAll<HTMLElement>('[data-word]')
            const next: number[] = []
            let line = -1
            let lastTop = Number.NEGATIVE_INFINITY

            masks.forEach(mask => {
                if (Math.abs(mask.offsetTop - lastTop) > 4) {
                    line += 1
                    lastTop = mask.offsetTop
                }

                next.push(line)
            })

            setLines(next)
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(element)

        return () => observer.disconnect()
    }, [text])

    return createElement(
        as,
        { ref, className: cn(className), id },
        words.map((word, index) => (
            <Fragment key={`${word}-${index}`}>
                <span data-word='' className='reveal-mask'>
                    <span
                        className={cn('reveal-word', inView && ready && 'is-in')}
                        style={{ transitionDelay: `${delay + (lines[index] ?? 0) * lineStagger}s` } as CSSProperties}
                    >
                        {word}
                    </span>
                </span>

                {index < words.length - 1 ? ' ' : null}
            </Fragment>
        ))
    )
}

interface ImageRevealProps {
    children: ReactNode
    className?: string
    delay?: number
}

/**
 * Soft mask wipe for images: the frame opens from the bottom while the
 * picture settles from a slight zoom.
 */
export const ImageReveal: FC<ImageRevealProps> = ({ children, className, delay = 0 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true, amount: 0.2 })
    const ready = useIntroReady()

    return (
        <div
            ref={ref}
            className={cn('img-reveal relative overflow-hidden', inView && ready && 'is-in', className)}
            style={{ transitionDelay: `${delay}s` }}
        >
            <div className='img-reveal-inner absolute inset-0' style={{ transitionDelay: `${delay}s` }}>
                {children}
            </div>
        </div>
    )
}
